import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Package, Folder, FolderPlus } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

import ProductFilters from './admin-products/ProductFilters';
import ProductForm from './admin-products/ProductForm';
import ProductList from './admin-products/ProductList';
import CategoryForm from './admin-products/CategoryForm';
import CategoryList from './admin-products/CategoryList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_PRODUCT = {
  name: '', description: '', price: '', category: '',
  unit: 'kg', unit_value: 1, availability: 'in_stock', min_order_quantity: 1, is_active: true, images: [],
};

const EMPTY_CATEGORY = {
  name: '', description: '', image: '', order: 0, is_active: true, default_unit: 'kg', default_unit_value: 1,
};

/**
 * AdminProducts orchestrator. Owns all state + API calls; delegates rendering to
 * ProductFilters / ProductForm / ProductList / CategoryForm / CategoryList.
 */
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [showProductForm, setShowProductForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categoryImage, setCategoryImage] = useState([]);
  const { authenticated, loading: authLoading, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY);

  useEffect(() => {
    if (!authLoading && !authenticated) navigate('/admin/login');
  }, [authenticated, authLoading, navigate]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${API}/admin/products${params}`, { headers: getAuthHeader() });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, getAuthHeader]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`, { headers: getAuthHeader() });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (authenticated) {
      fetchProducts();
      fetchCategories();
    }
  }, [authenticated, fetchProducts, fetchCategories]);

  // ----- Product handlers -----
  const resetProductForm = () => {
    setProductForm(EMPTY_PRODUCT);
    setShowProductForm(false);
    setEditingProduct(null);
  };

  const formatImagesForEdit = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images.map((img, idx) => {
        if (typeof img === 'string') {
          return { id: `img_${idx}_${Date.now()}`, url: img, is_primary: idx === 0, order: idx };
        }
        return {
          id: img.id || `img_${idx}_${Date.now()}`,
          url: img.url || img,
          is_primary: img.is_primary || idx === 0,
          order: img.order || idx,
        };
      });
    }
    if (product.imageUrl) {
      return [{ id: `img_0_${Date.now()}`, url: product.imageUrl, is_primary: true, order: 0 }];
    }
    return [];
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      category: product.category || '',
      min_order_quantity: product.min_order_quantity || 1,
      unit: product.unit || 'kg',
      unit_value: product.unit_value || 1,
      availability: product.availability || (product.stock > 0 ? 'in_stock' : 'out_of_stock'),
      is_active: product.is_active !== false,
      images: formatImagesForEdit(product),
    });
    setShowProductForm(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      const formattedImages = productForm.images.map((img, idx) => ({
        id: img.id || `img_${idx}_${Date.now()}`,
        url: img.url,
        is_primary: img.is_primary || false,
        order: idx,
      }));
      const primaryImage = formattedImages.find((img) => img.is_primary) || formattedImages[0];

      const data = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        category: productForm.category,
        min_order_quantity: parseFloat(productForm.min_order_quantity) || 1,
        unit: productForm.unit,
        unit_value: parseFloat(productForm.unit_value) || 1,
        availability: productForm.availability,
        is_active: productForm.is_active,
        images: formattedImages,
        imageUrl: primaryImage?.url || '',
      };

      if (editingProduct) {
        const pid = editingProduct._id || editingProduct.id;
        await axios.put(`${API}/admin/products/${pid}`, data, { headers: getAuthHeader() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, data, { headers: getAuthHeader() });
        toast.success('Product created');
      }
      resetProductForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId, name) => {
    if (!window.confirm(`Delete "${name}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers: getAuthHeader() });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleProductActive = async (product) => {
    try {
      const pid = product._id || product.id;
      await axios.put(`${API}/admin/products/${pid}`, { is_active: !product.is_active }, { headers: getAuthHeader() });
      toast.success(`Product ${product.is_active ? 'hidden' : 'shown'}`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  // ----- Category handlers -----
  const resetCategoryForm = () => {
    setCategoryForm({ ...EMPTY_CATEGORY, order: categories.length });
    setCategoryImage([]);
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      description: category.description || '',
      image: category.image || '',
      order: category.order || 0,
      is_active: category.is_active !== false,
      default_unit: category.default_unit || 'kg',
      default_unit_value: category.default_unit_value || 1,
    });
    setCategoryImage(category.image ? [{ id: 'existing', url: category.image, is_primary: true }] : []);
    setShowCategoryForm(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      toast.error('Category name is required');
      return;
    }
    try {
      const submitData = {
        ...categoryForm,
        image: categoryImage.length > 0 ? categoryImage[0].url : '',
      };
      if (editingCategory) {
        const cid = editingCategory._id || editingCategory.id;
        await axios.put(`${API}/admin/categories/${cid}`, submitData, { headers: getAuthHeader() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, submitData, { headers: getAuthHeader() });
        toast.success('Category created');
      }
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId, name) => {
    if (!window.confirm(`Delete category "${name}"? Products in this category will not be deleted.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, { headers: getAuthHeader() });
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Filter products by search term
  const filteredProducts = products.filter((p) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (p.name?.toLowerCase().includes(term)) || (p.description?.toLowerCase().includes(term));
  });

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-products-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Products &amp; Categories</h1>
        <p className="text-sm text-gray-500">Manage your product catalog and categories</p>
      </div>

      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'products' ? 'default' : 'outline'}
          onClick={() => setActiveTab('products')}
          data-testid="products-tab"
        >
          <Package className="h-4 w-4 mr-2" /> Products ({products.length})
        </Button>
        <Button
          variant={activeTab === 'categories' ? 'default' : 'outline'}
          onClick={() => setActiveTab('categories')}
          data-testid="categories-tab"
        >
          <Folder className="h-4 w-4 mr-2" /> Categories ({categories.length})
        </Button>
      </div>

      {activeTab === 'products' && (
        <>
          <ProductFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            categories={categories}
            onAddProduct={() => { resetProductForm(); setShowProductForm(true); }}
          />
          {showProductForm && (
            <ProductForm
              editingProduct={editingProduct}
              productForm={productForm}
              setProductForm={setProductForm}
              categories={categories}
              onSubmit={handleSaveProduct}
              onCancel={resetProductForm}
            />
          )}
          <ProductList
            loading={loading}
            products={filteredProducts}
            onToggleActive={toggleProductActive}
            onEdit={handleEditProduct}
            onDelete={handleDeleteProduct}
          />
        </>
      )}

      {activeTab === 'categories' && (
        <>
          <div className="flex justify-end mb-6">
            <Button onClick={() => { resetCategoryForm(); setShowCategoryForm(true); }} data-testid="add-category-btn">
              <FolderPlus className="h-4 w-4 mr-2" /> Add Category
            </Button>
          </div>
          {showCategoryForm && (
            <CategoryForm
              editingCategory={editingCategory}
              categoryForm={categoryForm}
              setCategoryForm={setCategoryForm}
              categoryImage={categoryImage}
              setCategoryImage={setCategoryImage}
              onSubmit={handleSaveCategory}
              onCancel={resetCategoryForm}
            />
          )}
          <CategoryList
            categories={categories}
            onEdit={handleEditCategory}
            onDelete={handleDeleteCategory}
          />
        </>
      )}
    </div>
  );
};

export default AdminProducts;
